import React from 'react';

const OTHER_PREFIX = 'Other:';
const OTHER_OPTION = 'other';

const toValueArray = (value) => {
  if (Array.isArray(value)) return value;
  return value === undefined || value === null || value === '' ? [] : [value];
};

const isCustomEntry = (v) => typeof v === 'string' && v.startsWith(`${OTHER_PREFIX} `);

const DropdownRenderer = ({ field, value, onChange, error, readOnly, multiple = false, freetext = false, placeholder = 'Select...' }) => {
  const allowsFreetext = freetext || field.type === 'dropdown_with_freetext' || field.type === 'multiselect_with_freetext';
  const values = toValueArray(value);
  const customEntry = values.find(isCustomEntry);
  const customText = customEntry ? customEntry.slice(OTHER_PREFIX.length + 1) : '';
  const otherSelected = values.includes(OTHER_OPTION) || !!customEntry;
  const showFreetext = allowsFreetext && otherSelected;

  const handleChange = (e) => {
    const selected = e.target.value;
    onChange(selected === OTHER_OPTION && customText ? `${OTHER_PREFIX} ${customText}` : selected);
  };

  const handleMultipleOptionChange = (option, checked) => {
    if (option === OTHER_OPTION) {
      const next = values.filter((v) => v !== OTHER_OPTION && !isCustomEntry(v));
      if (checked) {
        next.push(OTHER_OPTION);
        if (customEntry) next.push(customEntry);
      }
      onChange(next);
      return;
    }

    const next = checked
      ? [...values.filter((v) => v !== option), option]
      : values.filter((v) => v !== option);
    onChange(next);
  };

  const handleFreetextChange = (e) => {
    const text = e.target.value;
    if (multiple) {
      const base = values.filter((v) => !isCustomEntry(v));
      if (!base.includes(OTHER_OPTION)) base.push(OTHER_OPTION);
      onChange(text.trim() ? [...base, `${OTHER_PREFIX} ${text}`] : base);
      return;
    }
    onChange(text.trim() ? `${OTHER_PREFIX} ${text}` : OTHER_OPTION);
  };

  const options = field.options || [];
  // Keep saved values that are not declared options visible for legacy data
  // and quick-fill values.
  const extraValues = values.filter((v) => v && v !== OTHER_OPTION && !options.includes(v));
  const checkboxExtraValues = extraValues.filter((v) => !allowsFreetext || !isCustomEntry(v));
  const checkboxOptions = [...new Set([
    ...options,
    ...checkboxExtraValues,
  ])];

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.label}
        {multiple && <span className="text-gray-500 font-normal ml-1">(select all that apply)</span>}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {field.help_text && (
        <p className="text-xs text-gray-500 mb-2">{field.help_text}</p>
      )}
      {multiple ? (
        <div className={`border rounded-md p-2 ${error ? 'border-red-500' : 'border-gray-300'} ${readOnly ? 'bg-gray-100' : 'bg-white'}`}>
          <div className="max-h-48 space-y-1 overflow-y-auto">
            {checkboxOptions.map((option, index) => (
              <label
                key={`${option}-${index}`}
                className={`flex min-h-[44px] items-center rounded p-2 ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
              >
                <input
                  type="checkbox"
                  aria-label={`${field.label}: ${option}`}
                  checked={values.includes(option)}
                  onChange={(e) => handleMultipleOptionChange(option, e.target.checked)}
                  disabled={readOnly}
                  className="mr-2 h-[18px] w-[18px] shrink-0 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{option}</span>
              </label>
            ))}
            {allowsFreetext && (
              <label
                className={`flex min-h-[44px] items-center rounded p-2 ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
              >
                <input
                  type="checkbox"
                  aria-label={`${field.label}: Other (specify)`}
                  checked={otherSelected}
                  onChange={(e) => handleMultipleOptionChange(OTHER_OPTION, e.target.checked)}
                  disabled={readOnly}
                  className="mr-2 h-[18px] w-[18px] shrink-0 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Other (specify)</span>
              </label>
            )}
          </div>
        </div>
      ) : (
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={handleChange}
          disabled={readOnly}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-500' : 'border-gray-300'
          } ${readOnly ? 'bg-gray-100' : ''}`}
        >
          <option value="">{placeholder}</option>
          {options.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
          {extraValues.map((v) => (
            <option key={`custom-${v}`} value={v}>
              {v}
            </option>
          ))}
          {allowsFreetext && (
            <option value={OTHER_OPTION}>Other (specify)</option>
          )}
        </select>
      )}

      {showFreetext && (
        <input
          type="text"
          value={customText}
          onChange={handleFreetextChange}
          placeholder="Please specify..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md mt-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={readOnly}
        />
      )}

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

export default DropdownRenderer;
